import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, X, ShieldCheck, ShieldAlert, KeyRound, UserMinus, UserCheck } from 'lucide-react';
import { fmtDate } from '@/lib/format';

const empty = { email: '', name: '', password: '', role: 'admin' };

const RoleBadge = ({ role }) => {
  if (role === 'super_admin') return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold))] border border-[hsl(var(--sb-gold))]/40"><ShieldCheck size={10} /> Super Admin</span>;
  if (role === 'admin') return <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-[hsl(var(--sb-green))]/15 text-[hsl(var(--sb-green))] border border-[hsl(var(--sb-green))]/30">Admin</span>;
  return <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-white/5 text-white/70 border border-white/15">{role}</span>;
};

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(null);
  const [resetLink, setResetLink] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/users');
      setUsers(data || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error('Only super admins can manage users');
    }
  };
  useEffect(() => { load(); }, []);

  // Strictly enforce super_admin on the client side too (server already blocks)
  if (me && me.role !== 'super_admin') return <Navigate to="/admin" replace />;

  const create = async () => {
    if (!creating.email || !creating.name || !creating.password) { toast.error('All fields are required'); return; }
    if (creating.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      await api.post('/admin/users', creating);
      toast.success('User created');
      setCreating(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create user'); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}`, { role });
      toast.success(`Role updated to ${role.replace('_', ' ')}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const toggleDisable = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? 'User enabled' : 'User disabled');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const triggerReset = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/trigger-reset`);
      setResetLink({ email: u.email, url: data.reset_url });
      toast.success('Reset link generated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not trigger reset'); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-users-page">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-display text-3xl text-white inline-flex items-center gap-2"><ShieldCheck className="text-[hsl(var(--sb-gold))]" size={26} /> USER MANAGEMENT</h1>
          <button onClick={() => setCreating({ ...empty })} className="h-10 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold inline-flex items-center gap-2"><Plus size={16} />New user</button>
        </div>
        <p className="text-sm text-white/65 mb-5">Manage admin team members. Only Super Admins can view this page.</p>

        <div className="sb-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-white/55 bg-black/30">
              <tr>
                <th className="text-left px-4 py-3">Name / Email</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Last login</th>
                <th className="text-left px-4 py-3">Last password change</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[hsl(var(--sb-border))]">
                  <td className="px-4 py-3">
                    <div className="text-white">{u.name}</div>
                    <div className="text-[11px] text-white/55">{u.email}</div>
                  </td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-4 py-3">
                    {u.disabled
                      ? <span className="text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full bg-[hsl(var(--sb-red))]/15 text-[hsl(var(--sb-red))] border border-[hsl(var(--sb-red))]/30 inline-flex items-center gap-1"><ShieldAlert size={11} /> Disabled</span>
                      : <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--sb-green))]">Active</span>}
                  </td>
                  <td className="px-4 py-3 text-white/75">{u.last_login_at ? fmtDate(u.last_login_at) : <span className="text-white/40">never</span>}</td>
                  <td className="px-4 py-3 text-white/75">{u.password_changed_at ? fmtDate(u.password_changed_at) : <span className="text-[hsl(var(--sb-red))]">never{u.must_change_password ? ' · must change' : ''}</span>}</td>
                  <td className="px-4 py-3 text-white/75">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={u.id === me?.id} className="h-9 px-2 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white text-xs disabled:opacity-50">
                      <option value="super_admin">Super Admin</option>
                      <option value="admin">Admin</option>
                      <option value="customer">Customer</option>
                    </select>
                    <button onClick={() => triggerReset(u)} title="Send password reset" className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-[hsl(var(--sb-border))] text-[hsl(var(--sb-gold))] hover:border-[hsl(var(--sb-gold))]/60"><KeyRound size={14} /></button>
                    <button onClick={() => toggleDisable(u)} disabled={u.id === me?.id} title={u.disabled ? 'Enable' : 'Disable'} className={`h-9 w-9 inline-flex items-center justify-center rounded-md border border-[hsl(var(--sb-border))] disabled:opacity-40 ${u.disabled ? 'text-[hsl(var(--sb-green))]' : 'text-[hsl(var(--sb-red))]'}`}>{u.disabled ? <UserCheck size={14} /> : <UserMinus size={14} />}</button>
                  </td>
                </tr>
              ))}
              {!users.length && <tr><td colSpan={7} className="py-10 text-center text-white/55">No users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={() => setCreating(null)}>
          <div className="w-full max-w-md bg-[hsl(var(--sb-surface))] h-full overflow-y-auto border-l border-[hsl(var(--sb-border))]" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[hsl(var(--sb-border))] flex items-center justify-between">
              <div className="font-display text-2xl text-white">NEW USER</div>
              <button onClick={() => setCreating(null)} className="h-9 w-9 grid place-items-center text-white/70"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <F label="Name" value={creating.name} onChange={(v) => setCreating({ ...creating, name: v })} />
              <F label="Email" type="email" value={creating.email} onChange={(v) => setCreating({ ...creating, email: v })} />
              <F label="Temporary password (min 8)" type="password" value={creating.password} onChange={(v) => setCreating({ ...creating, password: v })} />
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-white/55">Role</span>
                <select value={creating.role} onChange={(e) => setCreating({ ...creating, role: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white">
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={create} className="flex-1 h-11 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold">Create user</button>
                <button onClick={() => setCreating(null)} className="h-11 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {resetLink && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center" onClick={() => setResetLink(null)}>
          <div className="w-full max-w-lg sb-card p-6 m-4" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl text-white">RESET LINK GENERATED</div>
            <p className="text-sm text-white/65 mt-1">For <strong>{resetLink.email}</strong>. The link has also been emailed (currently stubbed). Expires in 60 minutes.</p>
            <div className="mt-4 p-3 rounded-md bg-black/60 border border-[hsl(var(--sb-border))] font-mono text-[11px] text-[hsl(var(--sb-gold))] break-all">{resetLink.url}</div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { navigator.clipboard.writeText(resetLink.url); toast.success('Copied to clipboard'); }} className="h-10 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold">Copy link</button>
              <button onClick={() => setResetLink(null)} className="h-10 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white">Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const F = ({ label, value, onChange, type = 'text' }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-white/55">{label}</span>
    <input type={type} value={value === null || value === undefined ? '' : value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white" />
  </label>
);
